import { Container, Graphics } from 'pixi.js';
import { COLORS } from '@/config/colors';
import { t, type I18nKey } from '@/i18n';
import { cloneStats } from '@/upgrades/stats';
import type { CardContext, CardDef } from '@/upgrades/cards';
import type { WeaponHolder } from '@/entities/weapons';
import type { Player } from '@/entities/player';
import { WEAPONS, type WeaponCategory } from '@/config/weapons';
import { bodyText, chamferPanel, label } from './widgets';
import { drawCardIcon, iconTint } from './card-icons';
import { deltaGlyph } from './hangar-icons';
import {
  STAT_GROUPS, visibleRows, type RowState, type StatGroup, type StatView,
} from './stats-rows';

export type { StatView } from './stats-rows';

// Design-space metrics (docs/03 W4: glyphs ≥ 18 px, text ≥ 12 px)
const TITLE_H = 36;
const PAD_X = 14;
const GLYPH_X = 24;
const NAME_X = 42;
/** Card glyphs span ±18 units → 18 px */
const GLYPH_SCALE = 0.5;
const ROW_MAX = 26;
const ROW_MIN = 20;
const HEADER_H = 18;
const GROUP_GAP = 4;
const CHIP_H = 24;
const CHIP_GAP = 12;
/** Chip-local glyph centre / value start */
const CHIP_GLYPH_X = 10;
const CHIP_TEXT_X = 24;
const NAME_COLOR = 0xb4bac4;
const HEADER_COLOR = 0x7d8794;
const RULE_COLOR = 0x2a2440;
const OLD_VALUE_COLOR = 0x8a9099;
const VALUE_COLOR = 0xe8eaf0;
/** Crowded panels fold whole groups into glyph+value chips, least vital first */
const CHIP_ORDER: ReadonlyArray<StatGroup> = ['economy', 'mobility', 'defense', 'offense'];

interface Block {
  group: StatGroup;
  rows: RowState[];
  chips: RowState[];
}

/** Categories of the equipped weapons (category rows filter). */
function equippedCategories(weapon: WeaponHolder): WeaponCategory[] {
  return weapon.equipped.flatMap((id) => (WEAPONS[id] ? [WEAPONS[id]!.category] : []));
}

/**
 * Simulate buying `card` without touching the real run: cloned stats plus
 * shallow stand-ins for the two mutable weapon/player fields cards may write.
 */
function previewView(ctx: CardContext, card: CardDef): StatView {
  const stats = cloneStats(ctx.stats);
  const weapon = {
    gunTier: ctx.weapon.gunTier,
    rackSlots: ctx.weapon.rackSlots,
  } as WeaponHolder;
  const player = { hp: ctx.player.hp } as Player;
  card.apply({ stats, weapon, player, owned: ctx.owned });
  return { stats, gunTier: weapon.gunTier, hp: player.hp };
}

/**
 * Brotato-style ship status panel (docs/03 W4): only the stats the build
 * has changed, grouped OFFENSE / DEFENSE / MOBILITY / ECONOMY; category
 * rows only for equipped weapons. Focusing an offer card previews the
 * post-purchase value («old ▸ new»). `owned` mode (collection chips): only
 * highlight the rows the card touches — no fake "+again" delta.
 */
export function buildStatsPanel(
  w: number,
  h: number,
  ctx: CardContext,
  focused: CardDef | null,
  owned = false,
): Container {
  const current: StatView = {
    stats: ctx.stats,
    gunTier: ctx.weapon.gunTier,
    hp: ctx.player.hp,
    categories: equippedCategories(ctx.weapon),
  };
  return buildStatsPanelViews(w, h, current, focused ? previewView(ctx, focused) : null, owned);
}

/**
 * View-driven variant: the caller supplies both snapshots. Used by the
 * game-over screen to preview card REMOVAL (docs/12 C4) — deltas there
 * naturally render red because the stats get worse.
 */
export function buildStatsPanelViews(
  w: number,
  h: number,
  current: StatView,
  next: StatView | null,
  owned = false,
): Container {
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, { fill: 0x110e1b, stroke: 0x2a2440, notch: 16 });
  c.addChild(g);

  const title = label(t('statp.title'), 14, COLORS.uiAccent, 3);
  title.position.set(16, 12);
  c.addChild(title);

  const states = visibleRows(current, next);
  const blocks: Block[] = STAT_GROUPS
    .map((group) => ({ group, rows: states.filter((s) => s.row.group === group), chips: [] }))
    .filter((b) => b.rows.length > 0);
  const avail = h - TITLE_H - 10;
  const rowH = fitBlocks(blocks, avail, w);

  let y = TITLE_H;
  blocks.forEach((b, i) => {
    if (i > 0) y += GROUP_GAP;
    groupHeader(c, b.group, y, w);
    y += HEADER_H;
    for (const s of b.rows) {
      drawRow(c, s, y, rowH, w, owned);
      y += rowH;
    }
    y += drawChips(c, b.chips, y, w, owned);
  });

  // Stock ship: one quiet line instead of a wall of zeroes
  if (states.every((s) => s.row.always && s.next === null && s.cur === s.row.base)) {
    const hint = bodyText(t('statp.base'), 12, OLD_VALUE_COLOR, w - PAD_X * 2);
    hint.style.align = 'left';
    hint.position.set(PAD_X, y + 8);
    c.addChild(hint);
  }
  return c;
}

/**
 * Pick the row height; when even ROW_MIN overflows, fold groups into chip
 * flows (economy first). Rows the preview touches never fold, so the
 * «old ▸ new» delta stays readable. @returns the row height to use
 */
function fitBlocks(blocks: Block[], avail: number, w: number): number {
  const fixed = blocks.length * HEADER_H + Math.max(0, blocks.length - 1) * GROUP_GAP;
  const height = (rowH: number) => fixed + blocks.reduce(
    (sum, b) => sum + b.rows.length * rowH + chipFlowHeight(b.chips, w), 0,
  );
  const rows = blocks.reduce((n, b) => n + b.rows.length, 0);
  if (rows === 0) return ROW_MAX;
  for (const group of CHIP_ORDER) {
    if (height(ROW_MIN) <= avail) break;
    const b = blocks.find((x) => x.group === group);
    if (!b) continue;
    b.chips = b.rows.filter((s) => s.next === null);
    b.rows = b.rows.filter((s) => s.next !== null);
  }
  const left = blocks.reduce((n, b) => n + b.rows.length, 0);
  if (left === 0) return ROW_MAX;
  const chipH = blocks.reduce((sum, b) => sum + chipFlowHeight(b.chips, w), 0);
  return Math.max(ROW_MIN, Math.min(ROW_MAX, (avail - fixed - chipH) / left));
}

function groupHeader(c: Container, group: StatGroup, y: number, w: number): void {
  const head = label(t(`statp.group.${group}` as I18nKey), 12, HEADER_COLOR, 3);
  head.anchor.set(0, 0.5);
  head.position.set(PAD_X, y + HEADER_H / 2);
  c.addChild(head);
  const rule = new Graphics();
  rule.moveTo(PAD_X + head.width + 8, y + HEADER_H / 2).lineTo(w - PAD_X, y + HEADER_H / 2)
    .stroke({ color: RULE_COLOR, width: 1 });
  c.addChild(rule);
}

function glyph(icon: string, x: number, y: number): Graphics {
  const g = drawCardIcon(new Graphics(), icon, iconTint(icon));
  g.scale.set(GLYPH_SCALE);
  g.position.set(x, y);
  return g;
}

/** Semantic colour of a value at rest (docs/12 N5): bad red, good green. */
function restTone(s: RowState): number {
  const signed = s.row.fmt(s.cur).includes('%') && s.cur !== s.row.base;
  if (!signed) return VALUE_COLOR;
  const good = s.row.down ? s.cur < s.row.base : s.cur > s.row.base;
  return good ? COLORS.statUp : COLORS.statDown;
}

function drawRow(c: Container, s: RowState, y: number, rowH: number, w: number, owned: boolean): void {
  const cy = y + rowH / 2;
  const dir = s.better ? COLORS.statUp : COLORS.statDown;
  if (s.next !== null) {
    const band = new Graphics();
    band.roundRect(6, y + 1, w - 12, rowH - 2, 4).fill({ color: dir, alpha: 0.12 });
    c.addChild(band);
  }
  c.addChild(glyph(s.row.icon, GLYPH_X, cy));

  // Values right-aligned: «old ▸ new» on a preview, the plain value at rest
  const right = w - PAD_X;
  let left = right;
  const put = (text: string, size: number, color: number, gap: number): void => {
    const v = label(text, size, color, 0);
    v.anchor.set(1, 0.5);
    v.position.set(left, cy);
    c.addChild(v);
    left -= v.width + gap;
  };
  if (s.next !== null && !owned) {
    // ▲/▼ = which way the number moves; green/red = good or bad (wave 4)
    c.addChild(deltaGlyph(new Graphics(), left - 6, cy, 6, dir, s.next > s.cur));
    left -= 16;
    put(s.row.fmt(s.next), 13, dir, 4);
    put('▸', 12, dir, 4);
    put(s.row.fmt(s.cur), 12, OLD_VALUE_COLOR, 0);
  } else {
    put(s.row.fmt(s.cur), 13, s.next !== null ? dir : restTone(s), 0);
  }

  const name = label(t(s.row.key), 12, s.next !== null ? dir : NAME_COLOR, 1);
  name.anchor.set(0, 0.5);
  name.position.set(NAME_X, cy);
  const room = left - NAME_X - 8;
  if (name.width > room) name.scale.set(room / name.width);
  c.addChild(name);
}

/** Chip = glyph + value, no name (a crowded panel's compact form). */
function chipWidth(s: RowState): number {
  const v = label(s.row.fmt(s.cur), 13, VALUE_COLOR, 0);
  const width = CHIP_TEXT_X + v.width;
  v.destroy();
  return width;
}

function chipFlowHeight(chips: ReadonlyArray<RowState>, w: number): number {
  if (chips.length === 0) return 0;
  let lines = 1;
  let x = PAD_X;
  for (const s of chips) {
    const cw = chipWidth(s);
    if (x > PAD_X && x + cw > w - PAD_X) {
      lines++;
      x = PAD_X;
    }
    x += cw + CHIP_GAP;
  }
  return lines * CHIP_H;
}

/** @returns the height used */
function drawChips(
  c: Container, chips: ReadonlyArray<RowState>, y: number, w: number, owned: boolean,
): number {
  if (chips.length === 0) return 0;
  let x = PAD_X;
  let line = 0;
  for (const s of chips) {
    const cw = chipWidth(s);
    if (x > PAD_X && x + cw > w - PAD_X) {
      line++;
      x = PAD_X;
    }
    const cy = y + line * CHIP_H + CHIP_H / 2;
    c.addChild(glyph(s.row.icon, x + CHIP_GLYPH_X, cy));
    const tone = owned && s.next !== null ? (s.better ? COLORS.statUp : COLORS.statDown) : restTone(s);
    const v = label(s.row.fmt(s.cur), 13, tone, 0);
    v.anchor.set(0, 0.5);
    v.position.set(x + CHIP_TEXT_X, cy);
    c.addChild(v);
    x += cw + CHIP_GAP;
  }
  return (line + 1) * CHIP_H;
}
